import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  doc,
  getDocs,
  orderBy,
  Timestamp,
  serverTimestamp,
  updateDoc,
  getDoc,
  limit,
  writeBatch,
} from 'firebase/firestore'
import { db } from '../../firebase'
import { validateRange, ymdLocal } from '../utils/datetime'
import type { Lesson, CreateLessonInput, LessonStatus } from '../types/lesson'
import {
  createLessonCancelledNotification,
  createLessonRescheduledNotification,
} from '../../services/notificationService'

// SPAM-CLICK PROTECTION: Lock flag to prevent concurrent lesson creation
let isCreatingLesson = false

// Debug flag
export const DEBUG_CALENDAR = process.env.NODE_ENV === 'development'
export type LessonInput = {
  startAt: string // UTC ISO
  endAt: string // UTC ISO
  timezone: string // IANA timezone
  teacherId: string
  title?: string
  instrument?: string
  color?: string
  studentId?: string | null
  studentName?: string
  additionalStudents?: any[]
  status?: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELED'
  notes?: string
  location?: string
  durationMinutes: number
  lessonType?:
    | 'Private Lesson'
    | 'Group Lesson'
    | 'Trial Lesson'
    | 'Masterclass'
    | null
}
// P0 SCALABILITY FIX: Add query limit constant
const LESSON_QUERY_LIMIT = 100 // Maximum lessons to fetch per query (updated for busy teachers)
/**
 * Subscribe to lessons for a teacher within a date range
 * Collection: teachers/{teacherId}/calendar (subcollection)
 * Query: startAt range with LIMIT
 *
 * P0 FIX: Added limit() to prevent unbounded queries
 */
export function subscribeLessons({
  teacherId,
  from,
  to,
  onData,
}: {
  teacherId: string
  from: string
  to: string
  onData: (lessons: Lesson[]) => void
}): () => void {
  console.log('🔍 [QUERY/LISTENER] Subscribing to lessons with LIMIT:', {
    teacherId,
    from,
    to,
    limit: LESSON_QUERY_LIMIT,
    path: `teachers/${teacherId}/calendar`,
  })

  // FIX 1: Convert ISO strings to Firestore Timestamps for proper comparison
  const fromTimestamp = Timestamp.fromDate(new Date(from))
  const toTimestamp = Timestamp.fromDate(new Date(to))

  console.log('🔍 [FIX 1] Converted to Timestamps:', {
    fromISO: from,
    toISO: to,
    fromTimestamp: fromTimestamp.toDate().toISOString(),
    toTimestamp: toTimestamp.toDate().toISOString(),
  })

  // Query the teacher's calendar subcollection
  const teacherRef = doc(db, 'teachers', teacherId)
  const calendarCol = collection(teacherRef, 'calendar')

  // P0 FIX: Add limit() to query to prevent unbounded reads
  const q = query(
    calendarCol,
    where('startAt', '>=', fromTimestamp),
    where('startAt', '<=', toTimestamp),
    orderBy('startAt', 'asc'),
    limit(LESSON_QUERY_LIMIT), // ✅ CRITICAL: Limit query results
  )

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      // PROOF LOG: Snapshot size
      console.log('✅ [P0 FIX] Snapshot size (limited):', {
        size: snapshot.size,
        limit: LESSON_QUERY_LIMIT,
        hitLimit: snapshot.size === LESSON_QUERY_LIMIT,
      })

      if (snapshot.size > 0) {
        // PROOF LOG: First doc details
        const firstDoc = snapshot.docs[0]
        const firstData = firstDoc.data()
        console.log('✅ [FIX 1 PROOF] First doc:', {
          id: firstDoc.id,
          startAt:
            firstData.startAt?.toDate?.()?.toISOString() || firstData.startAt,
          durationMinutes: firstData.durationMinutes,
        })
      }

      console.log('🔍 [QUERY/LISTENER] Snapshot received:', {
        size: snapshot.size,
        empty: snapshot.empty,
        metadata: {
          hasPendingWrites: snapshot.metadata.hasPendingWrites,
          fromCache: snapshot.metadata.fromCache,
        },
      })

      const lessons = snapshot.docs.map((doc) => {
        const data = doc.data()
        // Convert Firestore Timestamps to ISO strings
        const startAt = data.startAt?.toDate?.()
          ? data.startAt.toDate().toISOString()
          : data.startAt
        const endAt = data.endAt?.toDate?.()
          ? data.endAt.toDate().toISOString()
          : data.endAt
        return {
          id: doc.id,
          ...data,
          startAt,
          endAt,
        } as Lesson
      })

      console.log('🔍 [QUERY/LISTENER] Converted lessons:', {
        count: lessons.length,
        firstThree: lessons.slice(0, 3).map((l) => ({
          id: l.id,
          startAt: l.startAt,
          startAtType: typeof l.startAt,
          durationMinutes: l.durationMinutes,
          teacherId: l.teacherId,
          timezone: l.timezone,
        })),
      })

      // Validate lessons have required fields
      const validLessons = lessons.filter((lesson) => {
        const isValid = lesson.startAt && lesson.endAt && lesson.timezone
        if (!isValid) {
          console.warn('🔍 [QUERY/LISTENER] Invalid lesson filtered out:', {
            id: lesson.id,
            hasStartAt: !!lesson.startAt,
            hasEndAt: !!lesson.endAt,
            hasTimezone: !!lesson.timezone,
          })
        }
        return isValid
      })

      console.log('🔍 [QUERY/LISTENER] Valid lessons after filtering:', {
        validCount: validLessons.length,
        invalidCount: lessons.length - validLessons.length,
      })

      onData(validLessons)
    },
    (error) => {
      console.error('🔍 [QUERY/LISTENER] Subscription error:', error)
    },
  )

  return unsubscribe
}
/**
 * Check if a duplicate lesson exists for the same student at the same time
 * @param teacherId - Teacher ID
 * @param studentId - Student ID
 * @param startAt - Lesson start time (Date object)
 * @returns Promise<boolean> - true if duplicate exists
 */
export async function checkForDuplicateLesson(
  teacherId: string,
  studentId: string,
  startAt: Date,
): Promise<boolean> {
  console.log('[LESSONS_SERVICE] Checking for duplicate lesson:', {
    teacherId,
    studentId,
    startAt: startAt.toISOString(),
  })

  try {
    const teacherRef = doc(db, 'teachers', teacherId)
    const calendarCol = collection(teacherRef, 'calendar')

    // Convert Date to Timestamp for Firestore query
    const startTimestamp = Timestamp.fromDate(startAt)

    // Query for lessons with same student and start time
    const q = query(
      calendarCol,
      where('studentId', '==', studentId),
      where('startAt', '==', startTimestamp),
    )

    const snapshot = await getDocs(q)

    const hasDuplicate = !snapshot.empty

    console.log('[LESSONS_SERVICE] Duplicate check result:', {
      hasDuplicate,
      matchingLessons: snapshot.size,
    })

    return hasDuplicate
  } catch (error) {
    console.error('[LESSONS_SERVICE] Error checking for duplicate:', error)
    // On error, allow creation (fail open)
    return false
  }
}
/**
 * Create a new lesson in Firestore
 * Collection: teachers/{teacherId}/calendar (subcollection)
 * Returns: Document ID
 *
 * @param input - Lesson creation input with local Date objects
 * @returns Promise resolving to the created document ID
 */
export async function createLesson(input: CreateLessonInput): Promise<string> {
  // SPAM-CLICK PROTECTION: Check if lesson creation is already in progress
  if (isCreatingLesson) {
    throw new Error('Lesson creation already in progress. Please wait.')
  }

  // SPAM-CLICK PROTECTION: Set lock before starting
  isCreatingLesson = true

  try {
    console.log('[LESSONS_SERVICE] createLesson called with input:', {
      ...input,
      startAt:
        input.startAt instanceof Date
          ? {
              iso: input.startAt.toISOString(),
              local: input.startAt.toLocaleString(),
              year: input.startAt.getFullYear(),
              month: input.startAt.getMonth() + 1,
              date: input.startAt.getDate(),
              hours: input.startAt.getHours(),
              minutes: input.startAt.getMinutes(),
            }
          : input.startAt,
    })

    // Validate required fields
    if (
      !input.title ||
      !input.studentId ||
      !input.teacherId ||
      !input.startAt ||
      !input.durationMin
    ) {
      const error = new Error(
        'Lesson requires: title, studentId, teacherId, startAt, durationMin',
      )
      console.error('[LESSONS_SERVICE] Validation error:', error)
      throw error
    }

    // Ensure startAt is a Date object
    let startAtDate: Date
    if (input.startAt instanceof Date) {
      startAtDate = input.startAt
      console.log('[LESSONS_SERVICE] startAt is already a Date object')
    } else if (typeof input.startAt === 'string') {
      startAtDate = new Date(input.startAt)
      console.log('[LESSONS_SERVICE] Converted startAt from string to Date')
    } else {
      throw new Error('startAt must be a Date object or ISO string')
    }

    // Validate the Date object is valid
    if (isNaN(startAtDate.getTime())) {
      throw new Error('Invalid startAt date')
    }

    console.log('[LESSONS_SERVICE] Using startAt Date:', {
      iso: startAtDate.toISOString(),
      local: startAtDate.toLocaleString('en-US', { timeZone: input.timezone }),
      year: startAtDate.getFullYear(),
      month: startAtDate.getMonth() + 1,
      date: startAtDate.getDate(),
      hours: startAtDate.getHours(),
      minutes: startAtDate.getMinutes(),
      timezone: input.timezone,
    })

    // CHECK FOR DUPLICATE LESSON
    const isDuplicate = await checkForDuplicateLesson(
      input.teacherId,
      input.studentId,
      startAtDate,
    )

    if (isDuplicate) {
      const error = new Error(
        'This student already has a lesson scheduled at this time. Please choose a different time.',
      )
      error.name = 'DuplicateLessonError'
      console.error('[LESSONS_SERVICE] Duplicate lesson detected:', {
        teacherId: input.teacherId,
        studentId: input.studentId,
        startAt: startAtDate.toISOString(),
      })
      throw error
    }

    // Compute endAt from startAt + durationMin
    const endAt = new Date(startAtDate.getTime() + input.durationMin * 60000)
    console.log('[LESSONS_SERVICE] Calculated endAt:', {
      iso: endAt.toISOString(),
      local: endAt.toLocaleString('en-US', { timeZone: input.timezone }),
    })

    // Convert to UTC ISO strings for logging
    const startAtUtc = startAtDate.toISOString()
    const endAtUtc = endAt.toISOString()
    console.log('[LESSONS_SERVICE] UTC times for validation:', {
      startAtUtc,
      endAtUtc,
      timezone: input.timezone,
    })

    // Validate the time range
    validateRange(startAtUtc, endAtUtc)

    // Resolve linkedUid from the student roster doc so the future Student
    // Dashboard lesson query can issue where('linkedUid','==',auth.uid).
    // Non-fatal: null on miss. This MUST run before addDoc so the field
    // is present at creation time and the backfill doesn't have to touch it.
    // 'createLesson' arg is PHASE 0 TEMP — remove in PR 0b.
    const linkedUid = await resolveLinkedUidForLesson(
      input.teacherId,
      input.studentId,
      'createLesson',
    )

    // Target: teachers/{teacherId}/calendar
    const teacherRef = doc(db, 'teachers', input.teacherId)
    const calendarCol = collection(teacherRef, 'calendar')

    // Prepare lesson data - store Date objects directly
    // Firestore will convert them to Timestamps automatically
    const payload = {
      teacherId: input.teacherId,
      studentId: input.studentId,
      studentName: input.studentName,
      // Denormalized access claim — stored at write time so future
      // student-side queries don't depend on a cross-doc rules read.
      linkedUid: linkedUid,
      title: input.title,
      startAt: startAtDate, // Firestore will convert to Timestamp
      endAt: endAt, // Firestore will convert to Timestamp
      durationMinutes: input.durationMin,
      location: input.location ?? null,
      instrument: input.instrument ?? null,
      notes: input.notes ?? null,
      timezone: input.timezone,
      lessonType: input.lessonType || null,
      status: 'SCHEDULED', // ✅ FIXED: Default status for new lessons (standardized)
      cancelled: false, // ADDED: Default cancelled state
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    console.log('[LESSONS_SERVICE] Payload to store in Firestore:', {
      ...payload,
      startAt: {
        type: 'Date',
        iso: startAtDate.toISOString(),
        local: startAtDate.toLocaleString('en-US', {
          timeZone: input.timezone,
        }),
        willBecomeTimestamp: true,
      },
      endAt: {
        type: 'Date',
        iso: endAt.toISOString(),
        local: endAt.toLocaleString('en-US', { timeZone: input.timezone }),
        willBecomeTimestamp: true,
      },
    })

    const docRef = await addDoc(calendarCol, payload)
    console.log('[LESSONS_SERVICE] ✅ Lesson created successfully!')
    console.log('[LESSONS_SERVICE] Document ID:', docRef.id)
    console.log(
      '[LESSONS_SERVICE] Path:',
      `teachers/${input.teacherId}/calendar/${docRef.id}`,
    )
    console.log(
      '[LESSONS_SERVICE] Expected date key:',
      ymdLocal(startAtUtc, input.timezone),
    )
    // PHASE 0a TEMP — [lessonWrite] instrumentation. Remove in PR 0b after
    // 24h burn-in. Sanity criterion: ZERO rows with
    // rosterHasLinkedUid:true && stampedLinkedUid:null over the window.
    // rosterFound / rosterHasLinkedUid are proxies derived from linkedUid
    // (precise per-roster signal lives in resolveLinkedUidForLesson logs).
    console.log('[lessonWrite]', {
      path: 'createLesson',
      teacherId: input.teacherId,
      studentId: input.studentId,
      rosterFound: linkedUid !== null,
      rosterHasLinkedUid: linkedUid !== null,
      stampedLinkedUid: linkedUid,
      lessonId: docRef.id,
    })
    return docRef.id
  } catch (error) {
    console.error('[LESSONS_SERVICE] ❌ Error creating lesson:', error)
    throw error
  } finally {
    // SPAM-CLICK PROTECTION: Always release lock, even on error
    isCreatingLesson = false
  }
}
/**
 * Update an existing lesson in Firestore
 * Collection: teachers/{teacherId}/calendar/{lessonId}
 *
 * @param lessonId - The ID of the lesson to update
 * @param teacherId - The ID of the teacher who owns the lesson
 * @param updates - Partial lesson object with fields to update
 * @returns Promise that resolves when update is complete
 */
export async function updateLesson(
  lessonId: string,
  teacherId: string,
  updates: Partial<Lesson>,
): Promise<void> {
  console.log('[LESSONS_SERVICE] 🔧 updateLesson called with:', {
    lessonId,
    teacherId,
    updates,
  })

  // Validate required parameters
  if (!lessonId || !teacherId) {
    const error = new Error('Lesson ID and Teacher ID are required')
    console.error('[LESSONS_SERVICE] ❌ Validation error:', error)
    throw error
  }

  // Validate that we're not trying to update immutable fields
  const immutableFields = ['id', 'createdAt']
  const invalidFields = Object.keys(updates).filter((key) =>
    immutableFields.includes(key),
  )
  if (invalidFields.length > 0) {
    const error = new Error(
      `Cannot update immutable fields: ${invalidFields.join(', ')}`,
    )
    console.error('[LESSONS_SERVICE] ❌ Validation error:', error)
    throw error
  }

  try {
    // Reference to the lesson document in the calendar subcollection
    const teacherRef = doc(db, 'teachers', teacherId)
    const lessonRef = doc(teacherRef, 'calendar', lessonId)
    console.log('[LESSONS_SERVICE] 📍 Document path:', {
      path: `teachers/${teacherId}/calendar/${lessonId}`,
      teacherRef: teacherRef.path,
      lessonRef: lessonRef.path,
    })

    // CRITICAL FIX: Get current lesson data BEFORE updating for notification comparison
    const currentLessonSnap = await getDoc(lessonRef)
    const currentLesson = currentLessonSnap.exists()
      ? currentLessonSnap.data()
      : null

    console.log('[LESSONS_SERVICE] 📖 Current lesson data fetched:', {
      exists: currentLessonSnap.exists(),
      hasData: !!currentLesson,
      currentStatus: currentLesson?.status,
    })

    // ATOMIC MERGE: Prepare update payload
    const updatePayload: any = {
      ...updates,
      updatedAt: serverTimestamp(),
    }

    // Convert Date objects or ISO strings to Firestore Timestamps if needed
    if (updates.startAt) {
      if (typeof updates.startAt === 'string') {
        updatePayload.startAt = new Date(updates.startAt)
        console.log('[LESSONS_SERVICE] 🕐 startAt conversion:', {
          original: updates.startAt,
          converted: updatePayload.startAt,
          iso: updatePayload.startAt.toISOString(),
        })
      } else if (updates.startAt instanceof Date) {
        updatePayload.startAt = updates.startAt
        console.log('[LESSONS_SERVICE] 🕐 startAt (Date object):', {
          date: updatePayload.startAt,
          iso: updatePayload.startAt.toISOString(),
        })
      }
    }

    if (updates.endAt) {
      if (typeof updates.endAt === 'string') {
        updatePayload.endAt = new Date(updates.endAt)
        console.log('[LESSONS_SERVICE] 🕐 endAt conversion:', {
          original: updates.endAt,
          converted: updatePayload.endAt,
          iso: updatePayload.endAt.toISOString(),
        })
      } else if (updates.endAt instanceof Date) {
        updatePayload.endAt = updates.endAt
        console.log('[LESSONS_SERVICE] 🕐 endAt (Date object):', {
          date: updatePayload.endAt,
          iso: updatePayload.endAt.toISOString(),
        })
      }
    }

    // Validate time range if both startAt and endAt are present
    if (updatePayload.startAt && updatePayload.endAt) {
      const startTime =
        updatePayload.startAt instanceof Date
          ? updatePayload.startAt.getTime()
          : new Date(updatePayload.startAt).getTime()
      const endTime =
        updatePayload.endAt instanceof Date
          ? updatePayload.endAt.getTime()
          : new Date(updatePayload.endAt).getTime()
      if (startTime >= endTime) {
        throw new Error('Start time must be before end time')
      }
      console.log('[LESSONS_SERVICE] ⏰ Time range validation:', {
        startTime,
        endTime,
        durationMs: endTime - startTime,
        durationMinutes: (endTime - startTime) / (1000 * 60),
      })
    }

    // Normalize status to uppercase if present
    if (updates.status) {
      updatePayload.status = String(updates.status).toUpperCase()
    }

    console.log('[LESSONS_SERVICE] 📦 Final update payload:', {
      ...updatePayload,
      startAt:
        updatePayload.startAt instanceof Date
          ? updatePayload.startAt.toISOString()
          : updatePayload.startAt,
      endAt:
        updatePayload.endAt instanceof Date
          ? updatePayload.endAt.toISOString()
          : updatePayload.endAt,
    })

    // ATOMIC UPDATE: Update the document in Firestore
    console.log('[LESSONS_SERVICE] 🚀 Executing Firestore update...')
    await updateDoc(lessonRef, updatePayload)
    console.log('[LESSONS_SERVICE] ✅ Firestore update completed!')

    // Create notifications for status changes (using data fetched BEFORE update)
    if (currentLesson) {
      // Check for cancellation
      if (
        updates.status === 'CANCELED' &&
        currentLesson.status !== 'CANCELED'
      ) {
        console.log('[LESSONS_SERVICE] 📢 Creating cancellation notification')
        await createLessonCancelledNotification(teacherId, {
          lessonId,
          studentId: currentLesson.studentId,
          studentName: currentLesson.studentName,
          title: currentLesson.title,
          startAt: currentLesson.startAt?.toDate?.() || currentLesson.startAt,
        }).catch((err) =>
          console.error('[LESSONS_SERVICE] Notification error:', err),
        )
      }

      // Check for reschedule (startAt changed but not cancelled)
      if (
        updates.startAt &&
        updates.status !== 'CANCELED' &&
        currentLesson.status !== 'CANCELED'
      ) {
        const oldStartAt =
          currentLesson.startAt?.toDate?.() || new Date(currentLesson.startAt)
        const newStartAt =
          updatePayload.startAt instanceof Date
            ? updatePayload.startAt
            : new Date(updatePayload.startAt)

        // Only create notification if times are actually different (more than 1 minute difference)
        if (Math.abs(oldStartAt.getTime() - newStartAt.getTime()) > 60000) {
          console.log('[LESSONS_SERVICE] 📢 Creating reschedule notification')
          await createLessonRescheduledNotification(teacherId, {
            lessonId,
            studentId: currentLesson.studentId,
            studentName: currentLesson.studentName,
            title: currentLesson.title,
            oldStartAt,
            newStartAt,
          }).catch((err) =>
            console.error('[LESSONS_SERVICE] Notification error:', err),
          )
        }
      }
    }

    // Verify the stored value by reading it back
    const docSnap = await getDoc(lessonRef)
    if (docSnap.exists()) {
      const storedData = docSnap.data()
      console.log('[LESSONS_SERVICE] 📖 Verified stored values in Firestore:', {
        startAt:
          storedData.startAt?.toDate?.()?.toISOString() || storedData.startAt,
        endAt: storedData.endAt?.toDate?.()?.toISOString() || storedData.endAt,
        status: storedData.status,
        cancelReason: storedData.cancelReason,
        cancelReasonNote: storedData.cancelReasonNote,
        cancelledAt: storedData.cancelledAt,
      })
    } else {
      console.warn('[LESSONS_SERVICE] ⚠️ Document not found after update')
    }
  } catch (error) {
    console.error('[LESSONS_SERVICE] ❌ Error updating lesson:', error)
    console.error('[LESSONS_SERVICE] Failed update details:', {
      lessonId,
      teacherId,
      updates,
    })
    throw error
  }
}

/**
 * Update recurring lessons based on scope
 * Queries by recurringGroupId and batch-updates docs.
 * For time changes, preserves each lesson's date but updates time-of-day.
 *
 * @param teacherId - The teacher who owns the lessons
 * @param lessonId - The specific lesson being edited
 * @param recurringGroupId - The shared recurring group ID
 * @param scope - 'this' | 'future' | 'all'
 * @param updates - The updates to apply
 * @param lessonStartAt - The startAt of the lesson being edited (for 'future' scope filtering)
 */
export async function updateRecurringLessons(
  teacherId: string,
  lessonId: string,
  recurringGroupId: string,
  scope: 'this' | 'future' | 'all',
  updates: Record<string, any>,
  lessonStartAt: string,
): Promise<number> {
  console.log('[LESSONS_SERVICE] updateRecurringLessons called:', {
    teacherId,
    lessonId,
    recurringGroupId,
    scope,
    updates,
    lessonStartAt,
  })

  if (!teacherId || !lessonId) {
    throw new Error('Teacher ID and Lesson ID are required')
  }

  // For 'this' scope, just update the single lesson
  if (scope === 'this' || !recurringGroupId) {
    await updateLesson(lessonId, teacherId, updates as Partial<Lesson>)
    return 1
  }

  try {
    const calendarRef = collection(db, 'teachers', teacherId, 'calendar')
    const q = query(
      calendarRef,
      where('recurringGroupId', '==', recurringGroupId),
    )
    const snapshot = await getDocs(q)

    console.log('[RECURRING EDIT] Total docs in series:', snapshot.size)

    // Determine which docs to update
    let docsToUpdate = snapshot.docs

    if (scope === 'future') {
      const selectedStartMs = new Date(lessonStartAt).getTime()
      docsToUpdate = snapshot.docs.filter((docSnap) => {
        const data = docSnap.data()
        const docStartAt = data.startAt?.toDate
          ? data.startAt.toDate()
          : new Date(data.startAt)
        return docStartAt.getTime() >= selectedStartMs
      })
      console.log(
        '[RECURRING EDIT] Filtered to',
        docsToUpdate.length,
        'future lessons',
      )
    }

    if (docsToUpdate.length === 0) {
      console.warn('[RECURRING EDIT] No matching lessons found')
      return 0
    }

    // Determine if this is a time change (startAt/endAt present in updates)
    const hasTimeChange = updates.startAt && updates.endAt
    // Extract the new time-of-day from the updates if it's a time change
    let newHours: number | null = null
    let newMinutes: number | null = null
    let newDurationMs: number | null = null

    if (hasTimeChange) {
      const newStart = new Date(updates.startAt)
      const newEnd = new Date(updates.endAt)
      newHours = newStart.getUTCHours()
      newMinutes = newStart.getUTCMinutes()
      newDurationMs = newEnd.getTime() - newStart.getTime()
      console.log('[RECURRING EDIT] Time change detected:', {
        newHours,
        newMinutes,
        newDurationMs,
      })
    }

    // Batch update
    const batch = writeBatch(db)

    docsToUpdate.forEach((docSnap) => {
      const docRef = doc(db, 'teachers', teacherId, 'calendar', docSnap.id)
      const data = docSnap.data()

      // Build per-doc updates
      const docUpdates: Record<string, any> = {
        updatedAt: serverTimestamp(),
      }

      // Copy non-time fields (notes, location, title, durationMinutes, etc.)
      const nonTimeFields = [
        'title',
        'notes',
        'location',
        'durationMinutes',
        'instrument',
      ]
      nonTimeFields.forEach((field) => {
        if (updates[field] !== undefined) {
          docUpdates[field] = updates[field]
        }
      })

      if (
        hasTimeChange &&
        newHours !== null &&
        newMinutes !== null &&
        newDurationMs !== null
      ) {
        // Preserve each lesson's date but update time-of-day
        const existingStartAt = data.startAt?.toDate
          ? data.startAt.toDate()
          : new Date(data.startAt)

        // Create new start with same date but new time (in UTC)
        const newStartAt = new Date(existingStartAt)
        newStartAt.setUTCHours(newHours, newMinutes, 0, 0)

        const newEndAt = new Date(newStartAt.getTime() + newDurationMs)

        docUpdates.startAt = newStartAt
        docUpdates.endAt = newEndAt
      }

      batch.update(docRef, docUpdates)
    })

    await batch.commit()
    console.log(
      '[RECURRING EDIT] Batch update committed:',
      docsToUpdate.length,
      'docs',
    )

    return docsToUpdate.length
  } catch (error) {
    console.error('[RECURRING EDIT] Error:', error)
    throw error
  }
}

/**
 * Mark a lesson as completed with attendance tracking.
 *
 * PHASE A FIX: routes through `updateLesson` so the write lands on the
 * canonical lesson path `teachers/{teacherId}/calendar/{lessonId}`.
 * Previously this wrote to `users/{teacherId}/lessons/{lessonId}`, which
 * silently created orphan documents at a phantom path and never updated
 * the real lesson — breaking reports, attendance metrics, and student-side
 * realtime sync.
 *
 * `updateLesson` also handles `updatedAt`, status normalization, and any
 * notification side-effects, so we don't duplicate that here.
 */
export async function completeLesson(
  teacherId: string,
  lessonId: string,
  attended: boolean = true,
): Promise<void> {
  if (!teacherId || !lessonId) {
    throw new Error('Teacher ID and Lesson ID are required')
  }

  await updateLesson(lessonId, teacherId, {
    status: 'COMPLETED',
    // ISO string preserved for downstream report/aggregation consumers
    // that expect completedAt as a string. `updateLesson` does not
    // transform this field, so the on-disk shape matches the previous
    // behaviour byte-for-byte aside from the correct collection path.
    completedAt: new Date().toISOString(),
    attended,
    // Invariant: a completed lesson cannot also be cancelled.
    cancelled: false,
  } as Partial<Lesson>)

  console.log(
    `[lessonsService] Lesson ${lessonId} marked as completed (attended: ${attended}) at teachers/${teacherId}/calendar/${lessonId}`,
  )
}

/**
 * Cancel a lesson with optional reason.
 *
 * PHASE A FIX: routes through `updateLesson` so the write lands on the
 * canonical lesson path. The existing `updateLesson` cancellation
 * notification path (createLessonCancelledNotification) fires
 * automatically when status transitions to CANCELED, so we don't
 * duplicate that here.
 */
export async function cancelLesson(
  teacherId: string,
  lessonId: string,
  cancelReason?: string,
): Promise<void> {
  if (!teacherId || !lessonId) {
    throw new Error('Teacher ID and Lesson ID are required')
  }

  const updates: Partial<Lesson> & {
    cancelled?: boolean
    attended?: boolean
    completedAt?: string | null
    cancelReason?: string
  } = {
    status: 'CANCELED',
    cancelled: true,
    attended: false,
    // Clear any previous completion timestamp — invariant: a cancelled
    // lesson cannot also be completed/attended.
    completedAt: null,
  }

  if (cancelReason && cancelReason.trim() !== '') {
    updates.cancelReason = cancelReason.trim()
  }

  await updateLesson(lessonId, teacherId, updates as Partial<Lesson>)

  console.log(
    `[lessonsService] Lesson ${lessonId} cancelled${cancelReason ? ` (reason: ${cancelReason})` : ''} at teachers/${teacherId}/calendar/${lessonId}`,
  )
}

/**
 * Update lesson status with proper completion/cancellation tracking.
 * Dispatcher that routes to the appropriate helper based on the target
 * status.
 *
 * PHASE A FIX: the SCHEDULED/RESCHEDULED branch now routes through
 * `updateLesson` (canonical path) instead of writing directly to the
 * wrong `users/{teacherId}/lessons/{lessonId}` path.
 */
export async function updateLessonStatus(
  teacherId: string,
  lessonId: string,
  status: LessonStatus,
  options?: {
    attended?: boolean
    cancelReason?: string
  },
): Promise<void> {
  if (!teacherId || !lessonId) {
    throw new Error('Teacher ID and Lesson ID are required')
  }

  if (status === 'COMPLETED') {
    await completeLesson(teacherId, lessonId, options?.attended ?? true)
  } else if (status === 'CANCELED') {
    await cancelLesson(teacherId, lessonId, options?.cancelReason)
  } else {
    // SCHEDULED / RESCHEDULED / other future statuses — route through
    // updateLesson so the write hits the canonical calendar path.
    await updateLesson(lessonId, teacherId, {
      status,
    } as Partial<Lesson>)
  }

  console.log(`[lessonsService] Lesson ${lessonId} status updated to ${status}`)
}

/**
 * Resolve the linkedUid for a given (teacherId, studentId) pair on a lesson.
 *
 * Reads the roster doc at teachers/{teacherId}/students/{studentId} and
 * returns its `linkedUid` field. Returns null if the doc is missing,
 * unreadable, or the student is not yet linked to a Firebase Auth user.
 *
 * Denormalizing this onto every calendar/lesson doc mirrors the proven
 * Tasks pattern (services/taskService.ts -> resolveLinkedUid) and lets
 * the future Student Dashboard lesson query take the form
 *   where('linkedUid', '==', auth.uid)
 * without needing a cross-doc rules read.
 *
 * Exported so the legacy modal write paths (AddLessonModal,
 * CreateLessonModal, CreateNewLessonModal recurring batch, studentService
 * calendar mirror writes) can share the same logic without duplicating it.
 * It MUST NOT throw — failure to resolve always degrades to null so a
 * missing/unlinked roster doc never blocks lesson creation.
 */
export const resolveLinkedUidForLesson = async (
  teacherId: string,
  studentId: string,
  // PHASE 0 INSTRUMENTATION — TEMPORARY ARG.
  // Identifies which write path called this resolver, so the [lessonWrite]
  // log below can be filtered per call site during the 24h validation
  // window. Default 'unknown' keeps the signature backwards-compatible
  // for any caller that hasn't been updated yet.
  // REMOVE this argument and the finally-block log in PR 0b after the
  // 24-hour observation window per CALENDAR_LINKEDUID_STAGE_D_PLAN_V3.
  callerPath: string = 'unknown',
): Promise<string | null> => {
  if (!teacherId || !studentId) return null

  // PHASE 0 INSTRUMENTATION — TEMPORARY LOCALS.
  // Capture the resolver's internal observations so the finally-block
  // log can publish the full picture (roster existence + link state)
  // alongside the stamped value. REMOVE in PR 0b.
  let rosterExists = false
  let rosterHasLinkedUid = false
  let stampedLinkedUid: string | null = null

  try {
    // FAST PATH: treat the passed value as the Firestore document ID.
    // This is the canonical shape and what new lessons should be storing.
    const rosterRef = doc(db, 'teachers', teacherId, 'students', studentId)
    const snap = await getDoc(rosterRef)
    if (snap.exists()) {
      rosterExists = true
      const data = snap.data() as { linkedUid?: string | null } | undefined
      if (data?.linkedUid && typeof data.linkedUid === 'string') {
        rosterHasLinkedUid = true
        stampedLinkedUid = data.linkedUid
      }
      // Doc exists at this ID but is not linked yet — return null without
      // attempting the fallback, since the fallback would just find the
      // same doc again.
      return stampedLinkedUid
    }

    // FALLBACK: the passed value is not a Firestore doc ID. Some legacy
    // lessons store the roster's inner `studentId` field (e.g.
    // "student-1779212948045") rather than the auto-generated doc ID
    // (e.g. "ESJwLKKPkM7I3E22kajU"). Resolve by querying the field.
    // Bounded by limit(1) so a duplicate roster won't blow up.
    const studentsRef = collection(db, 'teachers', teacherId, 'students')
    const fallbackQuery = query(
      studentsRef,
      where('studentId', '==', studentId),
      limit(1),
    )
    const result = await getDocs(fallbackQuery)
    if (result.empty) {
      return stampedLinkedUid
    }
    rosterExists = true
    const fallbackData = result.docs[0].data() as {
      linkedUid?: string | null
    }
    if (fallbackData?.linkedUid && typeof fallbackData.linkedUid === 'string') {
      rosterHasLinkedUid = true
      stampedLinkedUid = fallbackData.linkedUid
    }
    return stampedLinkedUid
  } catch (err) {
    console.warn(
      '[lessonsService] resolveLinkedUidForLesson failed; defaulting to null',
      { teacherId, studentId, err },
    )
    return null
  } finally {
    // PHASE 0 INSTRUMENTATION — TEMPORARY LOG.
    // Emits one [lessonWrite] line per resolver invocation, covering both
    // lesson-write call sites and the backfill sweep (use callerPath to
    // distinguish). Acceptance gate per CALENDAR_LINKEDUID_STAGE_D_PLAN_V3:
    // zero rows of { rosterHasLinkedUid: true, stampedLinkedUid: null }.
    // REMOVE the entire finally block in PR 0b after the 24h window.
    console.log('[lessonWrite]', {
      path: callerPath,
      teacherId,
      studentId,
      rosterExists,
      rosterHasLinkedUid,
      stampedLinkedUid,
    })
  }
}

/**
 * Idempotent backfill: ensure every calendar doc under
 * teachers/{teacherId}/calendar carries a `linkedUid` field denormalized
 * from its student roster doc.
 *
 * Self-heal behavior (Path A): docs whose `linkedUid` is currently null or
 * missing are re-resolved on every mount. Docs whose `linkedUid` is
 * already a valid non-empty string are skipped — those are NEVER
 * overwritten. This lets previously-null lessons automatically pick up
 * the correct uid the next time the teacher opens the dashboard after
 * the student has successfully linked their roster doc.
 *
 * Safety invariants:
 *  - Never deletes or replaces any other field on a calendar doc.
 *  - Never overwrites a valid existing string linkedUid.
 *  - Skips the write entirely when resolved null would equal existing
 *    null (no-op guard) — prevents pointless rewrites every mount when
 *    the student is still unlinked.
 *  - Per-mount write cap (WRITE_CAP_PER_MOUNT) bounds blast radius;
 *    remaining docs are picked up on subsequent mounts.
 *  - Batches capped at 450 (Firestore allows 500) for headroom.
 *  - Wrapped in try/catch — a failure here NEVER blocks the dashboard.
 *  - No Firestore security rule change required: the teacher's own
 *    credentials satisfy the existing `request.auth.uid == teacherId`
 *    allow rule.
 */
export const backfillTeacherCalendarLinkedUids = async (
  teacherId: string,
): Promise<{ scanned: number; updated: number; skipped: number }> => {
  if (!teacherId) return { scanned: 0, updated: 0, skipped: 0 }
  try {
    const calendarRef = collection(db, 'teachers', teacherId, 'calendar')
    const snapshot = await getDocs(calendarRef)
    const scanned = snapshot.size

    if (snapshot.empty) {
      console.log(
        '[backfillTeacherCalendarLinkedUids] no lessons found for teacher',
        teacherId,
      )
      return { scanned: 0, updated: 0, skipped: 0 }
    }

    // Per-mount safety cap on the number of docs we will write in a single
    // sweep. If a teacher has thousands of unlinked lessons, we heal up to
    // this many per mount; the rest will be picked up next mount.
    const WRITE_CAP_PER_MOUNT = 200

    // Per-student resolution cache so we only read each roster doc once.
    const linkedUidCache = new Map<string, string | null>()

    const toUpdate: {
      ref: ReturnType<typeof doc>
      linkedUid: string | null
    }[] = []
    let skipped = 0
    let capHit = false

    for (const docSnap of snapshot.docs) {
      // Per-mount write cap — stop queuing once we reach it.
      if (toUpdate.length >= WRITE_CAP_PER_MOUNT) {
        capHit = true
        break
      }

      const data = docSnap.data() as {
        studentId?: string
        linkedUid?: string | null
      }

      // INVARIANT: never overwrite a valid existing linkedUid.
      // Only skip when the field is already a non-empty string.
      // null / undefined fall through to re-resolution.
      const existing = data.linkedUid
      if (typeof existing === 'string' && existing.length > 0) {
        skipped++
        continue
      }

      // Orphan / malformed calendar entry — no studentId to resolve from.
      // Stamp null once so the doc carries the field; subsequent mounts
      // will hit the no-op guard below.
      if (!data.studentId || typeof data.studentId !== 'string') {
        if (existing === null) {
          // Already null, no studentId — nothing to do, no-op.
          skipped++
          continue
        }
        toUpdate.push({ ref: docSnap.ref, linkedUid: null })
        continue
      }

      let linkedUid: string | null
      if (linkedUidCache.has(data.studentId)) {
        linkedUid = linkedUidCache.get(data.studentId) as string | null
      } else {
        // 'backfill-sweep' arg is PHASE 0 TEMP — remove in PR 0b.
        linkedUid = await resolveLinkedUidForLesson(
          teacherId,
          data.studentId,
          'backfill-sweep',
        )
        linkedUidCache.set(data.studentId, linkedUid)
      }

      // No-op guard: if the doc is already null AND we still resolved
      // null, do NOT write. This is the key idempotency win — when a
      // student is still unlinked, null lessons are not rewritten on
      // every mount. They will heal automatically once the student
      // links and resolveLinkedUidForLesson returns a string.
      if (linkedUid === null && existing === null) {
        skipped++
        continue
      }

      toUpdate.push({ ref: docSnap.ref, linkedUid })
    }

    if (toUpdate.length === 0) {
      console.log('[backfillTeacherCalendarLinkedUids] nothing to backfill', {
        teacherId,
        scanned,
        skipped,
        capHit,
      })
      return { scanned, updated: 0, skipped }
    }

    // Batch writes (Firestore limit is 500; cap at 450 for headroom).
    const BATCH_LIMIT = 450
    let updated = 0
    for (let i = 0; i < toUpdate.length; i += BATCH_LIMIT) {
      const slice = toUpdate.slice(i, i + BATCH_LIMIT)
      const batch = writeBatch(db)
      slice.forEach(({ ref, linkedUid }) => {
        // batch.update — only touches the single `linkedUid` field.
        batch.update(ref, { linkedUid })
      })
      await batch.commit()
      updated += slice.length
    }

    console.log('[backfillTeacherCalendarLinkedUids] done', {
      teacherId,
      scanned,
      updated,
      skipped,
      capHit,
    })
    return { scanned, updated, skipped }
  } catch (err) {
    console.warn(
      '[backfillTeacherCalendarLinkedUids] failed; non-fatal, will retry next mount',
      err,
    )
    return { scanned: 0, updated: 0, skipped: 0 }
  }
}
